import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Activity, Wifi, TrendingUp } from "lucide-react";
import Sparkline from "@/components/Sparkline";
import { getLatency } from "@/lib/api";

const MAX_SAMPLES = 40;
const INTERVAL = 3000;

function stats(arr) {
  const v = arr.filter((x) => x != null);
  if (!v.length) return { cur: null, min: null, max: null, avg: null };
  return {
    cur: v[v.length - 1],
    min: Math.min(...v),
    max: Math.max(...v),
    avg: Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10,
  };
}

export default function LatencyMonitor({ open, onClose }) {
  const [router, setRouter] = useState([]);
  const [internet, setInternet] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const d = await getLatency();
        if (cancelled) return;
        setRouter((p) => [...p, d.router_latency_ms].slice(-MAX_SAMPLES));
        setInternet((p) => [...p, d.internet_latency_ms].slice(-MAX_SAMPLES));
      } catch {
        /* ignore */
      }
    };
    tick();
    timer.current = setInterval(tick, INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer.current);
    };
  }, [open]);

  const rs = stats(router);
  const is = stats(internet);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 z-30 bg-black/20 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 10 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            dir="rtl"
            className="absolute left-1/2 top-1/2 z-40 w-[420px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/60 bg-white/90 p-5 shadow-2xl backdrop-blur-xl"
            data-testid="latency-monitor"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className="text-[#0055FF]" />
                <h2 className="text-[15px] font-bold text-[#0A0A0A]">ניטור איחור חי</h2>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-[#52525B] transition-colors hover:bg-black/5"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mb-4 rounded-xl border border-black/5 bg-white/70 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0055FF]">
                  <Activity size={14} /> איחור לנתב
                </span>
                <span className="font-mono text-[13px] font-bold text-[#0A0A0A]">
                  {rs.cur != null ? `${rs.cur} ms` : "—"}
                </span>
              </div>
              <Sparkline data={router} color="#0055FF" />
              <div className="mt-1 flex justify-between font-mono text-[10px] text-[#94A3B8]">
                <span>min {rs.min ?? "—"}</span>
                <span>avg {rs.avg ?? "—"}</span>
                <span>max {rs.max ?? "—"}</span>
              </div>
            </div>

            <div className="rounded-xl border border-black/5 bg-white/70 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#8B5CF6]">
                  <Wifi size={14} /> איחור לאינטרנט
                </span>
                <span className="font-mono text-[13px] font-bold text-[#0A0A0A]">
                  {is.cur != null ? `${is.cur} ms` : "—"}
                </span>
              </div>
              <Sparkline data={internet} color="#8B5CF6" />
              <div className="mt-1 flex justify-between font-mono text-[10px] text-[#94A3B8]">
                <span>min {is.min ?? "—"}</span>
                <span>avg {is.avg ?? "—"}</span>
                <span>max {is.max ?? "—"}</span>
              </div>
            </div>

            <p className="mt-3 text-center text-[11px] text-[#94A3B8]">
              מתעדכן כל 3 שניות · {router.length} דגימות
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
